import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Check as CheckIcon,
  DirectionsBoat as BoatIcon,
  FileDownload as FileDownloadIcon,
  Anchor as AnchorIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Water as WaterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomerVessels } from '../../contexts/CustomerVesselContext';
import { doc, serverTimestamp, collection, getDocs, addDoc, query, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format } from 'date-fns';
import { getRoleById } from '../../firebase/services/roleService';
import { getAllSlips, upsertSlip, Slip } from '../../firebase/services/slipService';
import { debounce } from 'lodash';
import { useLocation } from 'react-router-dom';
import { findCustomerByName, Customer } from '../../firebase/services/customerVesselService';
import LaunchWarningDialog from '../../components/LaunchWarningDialog';

// Component for Launch Ramp Activity page
const LaunchRampPage: React.FC = () => {
  // Auth context to get user information
  const { userProfile, signOut } = useAuth();
  
  // Customer vessel context
  const { 
    customers,
    searchCustomers,
    searchVesselsByRegistration,
    searchVesselsByName,
    createCustomerVessel,
    getVesselLaunchActivity,
    addLaunchActivity,
    getTodayLaunchActivity,
    loading: customersLoading,
    error: customersError,
    refreshCustomers
  } = useCustomerVessels();
  
  // Search state with persistence
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  
  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<any[]>([]);
  
  // Vessel form state
  const [vesselRegistration, setVesselRegistration] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [vesselName, setVesselName] = useState('');
  const [vesselMake, setVesselMake] = useState('');
  const [vesselModel, setVesselModel] = useState('');
  const [vesselYear, setVesselYear] = useState<number | ''>('');
  const [vesselLength, setVesselLength] = useState<number | ''>('');
  const [vesselColor, setVesselColor] = useState('');
  const [mooringSlip, setMooringSlip] = useState('');
  
  // Customer form state
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerCompanyName, setCustomerCompanyName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // Seal form state (for hauling)
  const [sealNumber, setSealNumber] = useState('');
  const [sealColor, setSealColor] = useState('green');
  const [trailerLicense, setTrailerLicense] = useState('');
  const [aisSticker, setAisSticker] = useState('');
  const [notes, setNotes] = useState('');
  
  // Modal state
  const [vesselFormOpen, setVesselFormOpen] = useState(false);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [sealModalOpen, setSealModalOpen] = useState(false);
  const [removeSealModalOpen, setRemoveSealModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  
  // Action state
  const [actionType, setActionType] = useState<'launch' | 'haul'>('launch');
  const [selectedVesselId, setSelectedVesselId] = useState<string | null>(null);
  const [vesselHistory, setVesselHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  
  // New state for role
  const [roleName, setRoleName] = React.useState<string | null>(null);
  const [roleLoading, setRoleLoading] = React.useState<boolean>(true);
  
  // Add state for slip assignment prompt
  const [slipAssignDialogOpen, setSlipAssignDialogOpen] = useState(false);
  const [slipToAssign, setSlipToAssign] = useState<Slip | null>(null);
  const [pendingVesselId, setPendingVesselId] = useState<string | null>(null);
  
  // Add state for customer's assigned slips (array) and selected slip
  const [customerAssignedSlips, setCustomerAssignedSlips] = useState<Slip[]>([]);
  const [selectedSlipId, setSelectedSlipId] = useState<string | null>(null);
  
  // Add state for customer's assigned slip
  const [customerAssignedSlip, setCustomerAssignedSlip] = useState<Slip | null>(null);
  const [shouldAssignToSlip, setShouldAssignToSlip] = useState(false);
  
  // Add state for warning dialog
  const [warningDialog, setWarningDialog] = useState({
    open: false,
    message: ''
  });
  
  // Add state for existing customer confirmation dialog
  const [existingCustomerDialog, setExistingCustomerDialog] = useState({
    open: false,
    existingCustomer: null as Customer | null,
    onConfirm: () => {}
  });
  
  // Load vessel types
  useEffect(() => {
    const loadVesselTypes = async () => {
      try {
        // First attempt to get vessel types from settings collection
        const settingsRef = doc(db, 'settings', 'marinaSettings');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists() && settingsDoc.data().vesselTypes) {
          // Use vessel types from settings
          setVesselTypes(settingsDoc.data().vesselTypes);
        } else {
          // Fall back to checking the vesselTypes collection
          const typesQuery = query(collection(db, 'vesselTypes'));
          const typesSnapshot = await getDocs(typesQuery);
          
          if (!typesSnapshot.empty) {
            const types = typesSnapshot.docs.map(doc => doc.data().name);
            setVesselTypes(types);
          } else {
            // Default types if none found
            setVesselTypes(['Pontoon', 'Speedboat', 'Sailboat', 'Canoe', 'Kayak', 'Paddleboard']);
          }
        }
      } catch (err) {
        console.error('Error loading vessel types:', err);
        // Set default vessel types on error
        setVesselTypes(['Pontoon', 'Speedboat', 'Sailboat', 'Canoe', 'Kayak', 'Paddleboard']);
        setError('Warning: Using default vessel types');
        // Clear error after 3 seconds
        setTimeout(() => setError(null), 3000);
      }
    };
    
    loadVesselTypes();
  }, []);
  
  // New effect for role
  useEffect(() => {
    const fetchRole = async () => {
      if (userProfile && userProfile.roleId) {
        setRoleLoading(true);
        const roleDoc = await getRoleById(userProfile.roleId);
        setRoleName(roleDoc?.name || null);
        setRoleLoading(false);
      } else {
        setRoleName(null);
        setRoleLoading(false);
      }
    };
    fetchRole();
  }, [userProfile]);
  
  // Clear search results when authentication state changes (after re-authentication)
  useEffect(() => {
    console.log('🔄 LaunchRampPage: userProfile changed, clearing search state');
    console.log('📊 CustomerVesselContext state:', { 
      loading: customersLoading, 
      error: customersError,
      userProfileId: userProfile?.id,
      userProfileRole: userProfile?.roleId 
    });
    setSearchResults([]);
    setSearchQuery('');
    setSelectedCustomer(null);
    setError(null);
  }, [userProfile]);
  
  // Save search state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('launchRampSearchQuery', searchQuery);
    sessionStorage.setItem('launchRampSearchResults', JSON.stringify(searchResults));
  }, [searchQuery, searchResults]);
  
  // Clear search state from sessionStorage on every mount
  useEffect(() => {
    sessionStorage.removeItem('launchRampSearchQuery');
    sessionStorage.removeItem('launchRampSearchResults');
  }, []);
  
  // Handle Search with debounce
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a vessel registration number or name');
      return;
    }
    
    // Wait for CustomerVesselContext to finish loading after auth state changes
    if (customersLoading) {
      console.log('⏳ CustomerVesselContext still loading, skipping search');
      return;
    }
    
    // Check for context errors
    if (customersError) {
      console.log('❌ CustomerVesselContext has error:', customersError);
      setError(customersError);
      return;
    }
    
    // Check if user has proper role for searching
    if (!userProfile?.roleId || !roleName || !['staff', 'admin'].includes(roleName.toLowerCase())) {
      console.log('❌ User does not have proper role for searching:', { roleId: userProfile?.roleId, roleName });
      setError('Access restricted: You need staff or admin privileges to search vessels');
      return;
    }
    
    console.log('🔍 LaunchRampPage: Starting search for:', searchQuery);
    console.log('📊 Context state at search time:', {
      loading: customersLoading,
      error: customersError,
      userProfileId: userProfile?.id,
      userProfileRole: userProfile?.roleId,
      customersCount: customers.length
    });
    
    setSearching(true);
    setError(null);
    
    try {
      // Try searching by registration number first
      const registrationResults = await searchVesselsByRegistration(searchQuery);
      console.log('📋 Registration search results:', registrationResults.length);
      
      // If no results by registration, try by name
      const results = registrationResults.length > 0 
        ? registrationResults 
        : await searchVesselsByName(searchQuery);
      
      console.log('🚤 Final search results:', results.length);
      
      // Format results for display
      const formattedResults = results.map(result => ({
        ...result.vessel,
        id: result.vessel.vesselId,
        customerName: `${result.customer.firstName} ${result.customer.lastName}`,
        companyName: result.customer.companyName || '',
        customerId: result.customerId
      }));
      
      console.log('✅ Formatted search results:', formattedResults.length);
      setSearchResults(formattedResults);
    } catch (err) {
      console.error('❌ Error searching vessels:', err);
      setError('An error occurred while searching');
    } finally {
      setSearching(false);
    }
  };
  
  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce(() => {
      handleSearch();
    }, 300),
    [searchQuery]
  );
  
  // Effect to trigger search when query changes
  useEffect(() => {
    if (searchQuery.trim() && !customersLoading) {
      console.log('🔄 Triggering debounced search for:', searchQuery, 'loading:', customersLoading);
      debouncedSearch();
    } else if (customersLoading) {
      console.log('⏳ Delaying search due to loading state');
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, customersLoading, debouncedSearch]);
  
  // Handle vessel form open
  const handleOpenVesselForm = () => {
    // Pre-fill with search query if it's a registration number
    if (searchQuery && /^[A-Za-z0-9]+$/.test(searchQuery)) {
      setVesselRegistration(searchQuery.toUpperCase());
    }
    
    setVesselFormOpen(true);
  };
  
  // Handle vessel form close
  const handleCloseVesselForm = () => {
    setVesselFormOpen(false);
    resetVesselForm();
  };
  
  // Reset vessel form fields
  const resetVesselForm = () => {
    setVesselRegistration('');
    setVesselType('');
    setVesselName('');
    setVesselMake('');
    setVesselModel('');
    setVesselYear('');
    setVesselLength('');
    setVesselColor('');
    setMooringSlip('');
    setSelectedCustomer(null);
  };
  
  // Handle customer search
  const handleCustomerSearch = async () => {
    if (!customerSearchQuery.trim()) {
      return;
    }
    
    try {
      const results = await searchCustomers(customerSearchQuery);
      setCustomerSearchResults(results);
    } catch (err) {
      console.error('Error searching customers:', err);
      setError('An error occurred while searching for customers');
    }
  };
  
  // Handle customer selection
  const handleSelectCustomer = (customer: any) => {
    setSelectedCustomer(customer);
    setCustomerSearchResults([]);
    setCustomerSearchQuery('');
  };
  
  // Handle customer form open
  const handleOpenCustomerForm = () => {
    setCustomerFormOpen(true);
  };
  
  // Handle customer form close
  const handleCloseCustomerForm = () => {
    setCustomerFormOpen(false);
    resetCustomerForm();
  };
  
  // Reset customer form fields
  const resetCustomerForm = () => {
    setCustomerFirstName('');
    setCustomerLastName('');
    setCustomerCompanyName('');
    setCustomerEmail('');
    setCustomerPhone('');
  };
  
  // Handle customer form submit
  const handleCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if either company name OR first/last name is provided
    const hasCompanyName = !!customerCompanyName.trim();
    const hasFullName = !!(customerFirstName.trim() && customerLastName.trim());
    
    if (!hasCompanyName && !hasFullName) {
      setError('Please provide either a company name OR first and last name');
      return;
    }
    
    if (!customerEmail || !customerPhone) {
      setError('Please fill in all required customer fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // If we have a full name, check for existing customer
      if (hasFullName) {
        const existingCustomer = await findCustomerByName(
          customerFirstName.trim(),
          customerLastName.trim()
        );
        
        if (existingCustomer) {
          setExistingCustomerDialog({
            open: true,
            existingCustomer,
            onConfirm: async () => {
              setSelectedCustomer(existingCustomer);
              setCustomerFormOpen(false);
              resetCustomerForm();
              setExistingCustomerDialog({ open: false, existingCustomer: null, onConfirm: () => {} });
            }
          });
          setLoading(false);
          return;
        }
      }
      
      const newCustomerRef = await addDoc(collection(db, 'customers'), {
        firstName: customerFirstName,
        lastName: customerLastName,
        companyName: customerCompanyName,
        email: customerEmail,
        phone: customerPhone,
        vessels: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      const newCustomer = {
        id: newCustomerRef.id,
        firstName: customerFirstName,
        lastName: customerLastName,
        companyName: customerCompanyName,
        email: customerEmail,
        phone: customerPhone,
        vessels: []
      };
      
      // Refresh customers list from the database
      await refreshCustomers();
      setSelectedCustomer(newCustomer);
      setCustomerFormOpen(false);
      resetCustomerForm();
      setSuccess('Customer created successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating customer:', err);
      setError('Failed to create customer');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle vessel form submit with optimistic updates
  const handleVesselSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCustomer) {
      setError('Please select a customer first');
      return;
    }

    if (!vesselRegistration || !vesselType) {
      setError('Please fill in the required fields: Registration Number and Vessel Type');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create vessel data object with only defined values
      const vesselData: any = {
        registrationNumber: vesselRegistration.toUpperCase(),
        type: vesselType
      };

      // Only add optional fields if they have values
      if (vesselName) vesselData.name = vesselName;
      if (vesselMake) vesselData.make = vesselMake;
      if (vesselModel) vesselData.model = vesselModel;
      if (vesselYear) vesselData.year = Number(vesselYear);
      if (vesselLength) vesselData.length = Number(vesselLength);
      if (vesselColor) vesselData.color = vesselColor;
      if (mooringSlip) vesselData.mooringSlip = mooringSlip;
      
      // Optimistically update the UI
      const tempVesselId = `temp-${Date.now()}`;
      const tempVessel = {
        ...vesselData,
        vesselId: tempVesselId,
        status: 'active',
        addedAt: new Date().toISOString()
      };
      
      setSearchResults(prev => [...prev, {
        ...tempVessel,
        id: tempVesselId,
        customerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        customerId: selectedCustomer.id
      }]);
      
      // Create the vessel in the backend
      const vesselId = await createCustomerVessel(selectedCustomer.id, vesselData);
      
      // Update the temporary vessel with the real ID
      setSearchResults(prev => prev.map(v => 
        v.id === tempVesselId ? { ...v, id: vesselId, vesselId } : v
      ));
      
      setVesselFormOpen(false);
      resetVesselForm();
      setSuccess('Vessel created successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating vessel:', err);
      setError('Failed to create vessel');
      
      // Remove the temporary vessel on error
      setSearchResults(prev => prev.filter(v => !v.id.startsWith('temp-')));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle initiate vessel launch (open remove seal modal)
  const handleInitiateLaunch = async (vesselId: string) => {
    setSelectedVesselId(vesselId);
    setActionType('launch');

    // Find the vessel in search results to get customer information
    const vessel = searchResults.find(v => v.id === vesselId);
    if (vessel && vessel.customerId) {
      const slips = await getAllSlips();
      // Get all slips assigned to this customer and not already assigned to a vessel
      const assignedSlips = slips.filter(slip => slip.customerId === vessel.customerId && !slip.assignedVesselId);
      setCustomerAssignedSlips(assignedSlips);
      if (assignedSlips.length === 1) {
        setSelectedSlipId(assignedSlips[0].id);
        setCustomerAssignedSlip(assignedSlips[0]);
        setShouldAssignToSlip(false); // Default to not assigning
      } else {
        setSelectedSlipId(null);
        setCustomerAssignedSlip(null);
        setShouldAssignToSlip(false);
      }
    } else {
      setCustomerAssignedSlips([]);
      setSelectedSlipId(null);
      setCustomerAssignedSlip(null);
    }
    setRemoveSealModalOpen(true);
  };
  
  // Handle initiate vessel haul (open apply seal modal)
  const handleInitiateHaul = (vesselId: string) => {
    setSelectedVesselId(vesselId);
    setActionType('haul');
    setSealModalOpen(true);
  };
  
  // Handle view vessel history
  const handleViewHistory = async (vesselId: string) => {
    setSelectedVesselId(vesselId);
    setLoading(true);
    setError(null);
    
    try {
      const history = await getVesselLaunchActivity(vesselId);
      setVesselHistory(history);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error('Error loading vessel history:', err);
      setError('Failed to load vessel history');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle seal form submit (apply seal for haul)
  const handleSealSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedVesselId) {
      setError('No vessel selected');
      return;
    }
    
    if (!sealNumber || !trailerLicense) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Find the vessel to get customer information
      const vessel = searchResults.find(v => v.id === selectedVesselId);
      
      // Handle slip assignment and launch status updates
      if (actionType === 'launch') {
        // For launches, automatically assign vessel to customer's slip if they have one
        if (vessel && vessel.customerId) {
          const slips = await getAllSlips();
          const customerSlip = slips.find(slip => slip.customerId === vessel.customerId && !slip.assignedVesselId);
          
          if (customerSlip) {
            // Check payment status before proceeding
            if (customerSlip.paymentStatus !== 'paid' && roleName !== 'admin') {
              setWarningDialog({
                open: true,
                message: `WARNING: This slip has a payment status of "${customerSlip.paymentStatus}". DO NOT LAUNCH THE BOAT. Please check with management before proceeding.`
              });
              setLoading(false);
              return;
            }
            
            // Automatically assign vessel to slip and set launch status
            await updateDoc(doc(db, 'slips', customerSlip.id), {
              assignedVesselId: selectedVesselId,
              status: 'occupied',
              launchStatus: 'launched',
              updatedAt: Timestamp.now()
            });
            console.log(`✅ Automatically assigned vessel ${selectedVesselId} to slip ${customerSlip.slipNumber || customerSlip.id} and set launch status to 'launched'`);
          }
        }
      } else if (actionType === 'haul') {
        // For hauls, find the slip with this vessel and update launch status
        console.log(`🔍 Starting haul process for vessel: ${selectedVesselId}`);
        
        try {
          const slips = await getAllSlips();
          console.log(`📋 Retrieved ${slips.length} slips to search for vessel ${selectedVesselId}`);
          
          const vesselSlip = slips.find(slip => slip.assignedVesselId === selectedVesselId);
          
          if (vesselSlip) {
            console.log(`✅ Found slip ${vesselSlip.slipNumber || vesselSlip.id} assigned to vessel ${selectedVesselId}`);
            console.log(`🚢 Current slip launch status: ${vesselSlip.launchStatus || 'undefined'}`);
            
            // Update launch status to not_launched and unassign vessel for hauls
            await updateDoc(doc(db, 'slips', vesselSlip.id), {
              launchStatus: 'not_launched',
              assignedVesselId: null, // Remove vessel assignment when hauled
              updatedAt: Timestamp.now()
            });
            console.log(`✅ Successfully updated slip ${vesselSlip.slipNumber || vesselSlip.id} launch status to 'not_launched' and unassigned vessel for haul`);
            
            // Verify the update by re-fetching the slip
            const updatedSlipDoc = await getDoc(doc(db, 'slips', vesselSlip.id));
            if (updatedSlipDoc.exists()) {
              const updatedData = updatedSlipDoc.data();
              console.log(`🔍 Verification: Slip ${vesselSlip.slipNumber || vesselSlip.id} launch status is now: ${updatedData.launchStatus}, assigned vessel: ${updatedData.assignedVesselId || 'none'}`);
            }
          } else {
            console.log(`⚠️ No slip found with assigned vessel ID: ${selectedVesselId}`);
            console.log(`📊 Available slip assignments:`, slips.filter(s => s.assignedVesselId).map(s => ({ 
              slipId: s.id, 
              slipNumber: s.slipNumber, 
              assignedVesselId: s.assignedVesselId,
              launchStatus: s.launchStatus 
            })));
          }
        } catch (slipUpdateError) {
          console.error(`❌ Error updating slip launch status for haul:`, slipUpdateError);
          throw slipUpdateError; // Re-throw to be caught by the outer try-catch
        }
      }
      
      // Create activity data object
      const activityData = {
        type: actionType,
        sealNumber,
        sealColor,
        trailerLicense,
        aisSticker,
        notes,
        timestamp: new Date(),
        staffName: userProfile?.firstName?.[0] + userProfile?.lastName?.[0] || 'SS'
      };
      
      let tempActivityId: string | undefined;
      
      // Optimistically update the UI if history modal is open
      if (historyModalOpen) {
        tempActivityId = `temp-${Date.now()}`;
        setVesselHistory(prev => [...prev, {
          ...activityData,
          id: tempActivityId,
          vesselId: selectedVesselId
        }]);
      }
      
      // Add the activity in the backend
      const activityId = await addLaunchActivity(selectedVesselId, activityData);
      
      // Update the temporary activity with the real ID
      if (historyModalOpen && tempActivityId) {
        setVesselHistory(prev => prev.map(a => 
          a.id === tempActivityId ? { ...a, id: activityId } : a
        ));
      }
      
      setSealModalOpen(false);
      setRemoveSealModalOpen(false);
      resetSealForm();
      setCustomerAssignedSlip(null);
      setShouldAssignToSlip(false);
      
      setSuccess(`Vessel ${actionType === 'launch' ? 'launched' : 'hauled'} successfully`);
      
      // If this was a launch, log out the user
      if (actionType === 'launch') {
        // Clear success message after 2 seconds
        setTimeout(() => {
          setSuccess(null);
          // Log out the user
          signOut();
        }, 2000);
      } else {
        // For hauls, just clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error(`Error ${actionType === 'launch' ? 'launching' : 'hauling'} vessel:`, err);
      setError(`Failed to ${actionType} vessel`);
      
      // Remove the temporary activity on error
      if (historyModalOpen) {
        setVesselHistory(prev => prev.filter(a => !a.id.startsWith('temp-')));
      }
      
      // Revert slip assignment if it failed
      if (shouldAssignToSlip && customerAssignedSlip) {
        setCustomerAssignedSlip({ ...customerAssignedSlip, assignedVesselId: null });
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Reset seal form fields
  const resetSealForm = () => {
    setSealNumber('');
    setSealColor('green');
    setTrailerLicense('');
    setAisSticker('');
    setNotes('');
    setCustomerAssignedSlip(null);
    setShouldAssignToSlip(false);
  };
  
  // Handle export activity to Excel
  const handleExportActivity = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const activities = await getTodayLaunchActivity();
      
      // Download a CSV file as a placeholder
      const csv = [
        ['Date', 'Time', 'Action', 'Vessel ID', 'Trailer License', 'Seal Number', 'Seal Color', 'Staff', 'Notes'],
        ...activities.map((a: any) => [
          format(new Date(a.timestamp), 'MM/dd/yyyy'),
          format(new Date(a.timestamp), 'hh:mm a'),
          a.type === 'launch' ? 'Launch' : 'Haul',
          a.vesselId,
          a.trailerLicense,
          a.sealNumber,
          a.sealColor,
          a.staffName,
          a.notes
        ])
      ]
        .map(row => row.map(cell => `"${cell || ''}"`).join(','))
        .join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `launch_activity_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setSuccess('Activity exported successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error exporting activity:', err);
      setError('Failed to export activity');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportActivity}
            sx={{ mr: 2 }}
          >
            Export Today's Activity
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {/* Search Section */}
        <Box mb={4}>
          <TextField
            fullWidth
            label="Search Vessels"
            variant="outlined"
            size="medium"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // Clear search results if the search field is empty
              if (!e.target.value.trim()) {
                setSearchResults([]);
              }
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton onClick={() => {
                      setSearchQuery('');
                      // Clear search results when the clear button is clicked
                      setSearchResults([]);
                    }}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={handleSearch} disabled={searching}>
                    {searching ? <CircularProgress size={24} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {/* Search Results */}
        {searchResults.length > 0 ? (
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Search Results
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vessel Name</TableCell>
                    <TableCell>Registration</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Make</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Length</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((vessel) => (
                    <TableRow key={vessel.id}>
                      <TableCell>{vessel.name || 'Unnamed Vessel'}</TableCell>
                      <TableCell>{vessel.registrationNumber ? vessel.registrationNumber.toUpperCase() : 'N/A'}</TableCell>
                      <TableCell>{vessel.companyName || vessel.customerName}</TableCell>
                      <TableCell>{vessel.type || 'N/A'}</TableCell>
                      <TableCell>{vessel.make || 'N/A'}</TableCell>
                      <TableCell>{vessel.model || 'N/A'}</TableCell>
                      <TableCell>{vessel.length ? `${vessel.length} ft` : 'N/A'}</TableCell>
                      <TableCell>{vessel.color || 'N/A'}</TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleInitiateLaunch(vessel.id)}
                          title="Remove Seal"
                        >
                          <WaterIcon />
                        </IconButton>
                        <IconButton
                          color="warning"
                          onClick={() => handleInitiateHaul(vessel.id)}
                          title="Apply Seal"
                        >
                          <AnchorIcon />
                        </IconButton>
                        <IconButton
                          color="info"
                          onClick={() => handleViewHistory(vessel.id)}
                          title="Vessel History"
                        >
                          <HistoryIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Always show Register New Vessel button if there is a search query */}
            {searchQuery && (
              <Box textAlign="center" mt={2}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenVesselForm}
                >
                  Register New Vessel
                </Button>
              </Box>
            )}
          </Box>
        ) : searchQuery && !searching ? (
          <Box textAlign="center" my={4} p={3} bgcolor="background.paper" borderRadius={1}>
            <Typography variant="h6" gutterBottom>
              No vessels found matching "{searchQuery}"
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenVesselForm}
            >
              Register New Vessel
            </Button>
          </Box>
        ) : null}
        
        {/* Vessel Registration Dialog */}
        <Dialog
          open={vesselFormOpen}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleCloseVesselForm();
          }}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            Vessel Registration
            <IconButton
              onClick={handleCloseVesselForm}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Customer Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                
                {!selectedCustomer ? (
                  <>
                    <TextField
                      fullWidth
                      label="Search customer by name, email, or phone"
                      variant="outlined"
                      margin="normal"
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCustomerSearch()}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleCustomerSearch}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    {customerSearchResults.length > 0 ? (
                      <Box mt={2} maxHeight={200} overflow="auto">
                        {customerSearchResults.map((customer) => (
                          <Box
                            key={customer.id}
                            p={2}
                            mb={1}
                            border={1}
                            borderColor="divider"
                            borderRadius={1}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <Typography variant="subtitle1">
                                {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                              </Typography>
                              {customer.companyName && (
                                <Typography variant="body2" color="text.secondary">
                                  {customer.firstName} {customer.lastName}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                {customer.email}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {customer.phone}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleSelectCustomer(customer)}
                              startIcon={<CheckIcon />}
                            >
                              Select
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      customerSearchQuery && (
                        <Box mt={2} textAlign="center">
                          <Typography variant="body1" gutterBottom>
                            No customers found
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleOpenCustomerForm}
                            startIcon={<AddIcon />}
                          >
                            Create New Customer
                          </Button>
                        </Box>
                      )
                    )}
                    
                    {!customerSearchQuery && (
                      <Box mt={2} textAlign="center">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleOpenCustomerForm}
                          startIcon={<AddIcon />}
                        >
                          Create New Customer
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
                    <Typography variant="subtitle1" gutterBottom>
                      Selected Customer:
                    </Typography>
                    <Typography variant="h6">
                      {selectedCustomer.companyName || `${selectedCustomer.firstName} ${selectedCustomer.lastName}`}
                    </Typography>
                    {selectedCustomer.companyName && (
                      <Typography variant="body1">
                        {selectedCustomer.firstName} {selectedCustomer.lastName}
                      </Typography>
                    )}
                    <Typography variant="body1">{selectedCustomer.email}</Typography>
                    <Typography variant="body1">{selectedCustomer.phone}</Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => setSelectedCustomer(null)}
                      sx={{ mt: 2 }}
                    >
                      Change Customer
                    </Button>
                  </Box>
                )}
              </Grid>
              
              {/* Vessel Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Vessel Information
                </Typography>
                <form onSubmit={handleVesselSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Registration Number"
                        value={vesselRegistration}
                        onChange={(e) => setVesselRegistration(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Vessel Type</InputLabel>
                        <Select
                          value={vesselType}
                          onChange={(e) => setVesselType(e.target.value)}
                          label="Vessel Type"
                        >
                          {vesselTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Vessel Name"
                        value={vesselName}
                        onChange={(e) => setVesselName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Make"
                        value={vesselMake}
                        onChange={(e) => setVesselMake(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Model"
                        value={vesselModel}
                        onChange={(e) => setVesselModel(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Year"
                        type="number"
                        inputProps={{ min: 1900, max: new Date().getFullYear() }}
                        value={vesselYear}
                        onChange={(e) => setVesselYear(e.target.value === '' ? '' : Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Length (ft)"
                        type="number"
                        inputProps={{ min: 1, max: 100 }}
                        value={vesselLength}
                        onChange={(e) => setVesselLength(e.target.value === '' ? '' : Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Color"
                        value={vesselColor}
                        onChange={(e) => setVesselColor(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </form>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVesselForm}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleVesselSubmit}
              disabled={!selectedCustomer || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Register Vessel'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Customer Form Dialog */}
        <Dialog
          open={customerFormOpen}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            handleCloseCustomerForm();
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            New Customer Registration
            <IconButton
              onClick={handleCloseCustomerForm}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <form onSubmit={handleCustomerSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={customerCompanyName}
                    onChange={(e) => setCustomerCompanyName(e.target.value)}
                    helperText="Required if no individual name is provided"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={customerFirstName}
                    onChange={(e) => setCustomerFirstName(e.target.value)}
                    helperText="Required if no company name is provided"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={customerLastName}
                    onChange={(e) => setCustomerLastName(e.target.value)}
                    helperText="Required if no company name is provided"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Phone"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCustomerForm}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCustomerSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Customer'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Apply Seal (Haul) Dialog */}
        <Dialog
          open={sealModalOpen}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            setSealModalOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            Apply Seal
            <IconButton
              onClick={() => setSealModalOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <form onSubmit={handleSealSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Trailer License Plate"
                    value={trailerLicense}
                    onChange={(e) => setTrailerLicense(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="AIS Sticker Number"
                    value={aisSticker}
                    onChange={(e) => setAisSticker(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    required
                    label="Seal Number"
                    value={sealNumber}
                    onChange={(e) => setSealNumber(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Seal Color</InputLabel>
                    <Select
                      value={sealColor}
                      onChange={(e) => setSealColor(e.target.value)}
                      label="Seal Color"
                    >
                      <MenuItem value="green">Green</MenuItem>
                      <MenuItem value="red" disabled={roleName !== 'admin'}>Red</MenuItem>
                      <MenuItem value="blue" disabled={roleName !== 'admin'}>Blue</MenuItem>
                      <MenuItem value="yellow" disabled={roleName !== 'admin'}>Yellow</MenuItem>
                      <MenuItem value="white" disabled={roleName !== 'admin'}>White</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSealModalOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSealSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Complete Apply Seal'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Remove Seal (Launch) Dialog */}
        <Dialog
          open={removeSealModalOpen}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            setRemoveSealModalOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            Remove Seal
            <IconButton
              onClick={() => setRemoveSealModalOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <form onSubmit={handleSealSubmit}>
              <Grid container spacing={2}>
                {/* Slip assignment UI for launch */}
                {(customerAssignedSlips.length > 1) && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Customer has multiple assigned slips:
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Slip</InputLabel>
                        <Select
                          value={selectedSlipId || ''}
                          label="Select Slip"
                          onChange={e => {
                            setSelectedSlipId(e.target.value);
                            const slip = customerAssignedSlips.find(s => s.id === e.target.value);
                            setCustomerAssignedSlip(slip || null);
                          }}
                        >
                          {customerAssignedSlips.map(slip => (
                            <MenuItem key={slip.id} value={slip.id}>
                              {slip.slipNumber || slip.id} (Status: {slip.paymentStatus})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Button
                          variant={shouldAssignToSlip ? "contained" : "outlined"}
                          color="primary"
                          size="small"
                          onClick={() => {
                            const slip = customerAssignedSlips.find(s => s.id === selectedSlipId);
                            if (slip && slip.paymentStatus !== 'paid' && roleName !== 'admin') {
                              setWarningDialog({
                                open: true,
                                message: `WARNING: This slip has a payment status of "${slip.paymentStatus}". DO NOT LAUNCH THE BOAT. Please check with management before proceeding.`
                              });
                              setShouldAssignToSlip(false);
                            } else {
                              setShouldAssignToSlip(true);
                            }
                          }}
                          sx={{ mr: 1 }}
                          disabled={!selectedSlipId}
                        >
                          Yes, Assign
                        </Button>
                        <Button
                          variant={!shouldAssignToSlip ? "contained" : "outlined"}
                          color="primary"
                          size="small"
                          onClick={() => setShouldAssignToSlip(false)}
                        >
                          No, Skip
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                )}
                {/* Single slip UI (legacy) */}
                {customerAssignedSlip && customerAssignedSlips.length <= 1 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Customer's Assigned Slip: {customerAssignedSlip.slipNumber || customerAssignedSlip.id}
                      </Typography>
                      <FormControl component="fieldset">
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Would you like to assign this vessel to the customer's slip?
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Button
                            variant={shouldAssignToSlip ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            onClick={() => {
                              if (customerAssignedSlip.paymentStatus !== 'paid' && roleName !== 'admin') {
                                setWarningDialog({
                                  open: true,
                                  message: `WARNING: This slip has a payment status of "${customerAssignedSlip.paymentStatus}". DO NOT LAUNCH THE BOAT. Please check with management before proceeding.`
                                });
                                setShouldAssignToSlip(false);
                              } else {
                                setShouldAssignToSlip(true);
                              }
                            }}
                            sx={{ mr: 1 }}
                          >
                            Yes, Assign
                          </Button>
                          <Button
                            variant={!shouldAssignToSlip ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            onClick={() => setShouldAssignToSlip(false)}
                          >
                            No, Skip
                          </Button>
                        </Box>
                      </FormControl>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Trailer License Plate"
                    value={trailerLicense}
                    onChange={(e) => setTrailerLicense(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="AIS Sticker Number"
                    value={aisSticker}
                    onChange={(e) => setAisSticker(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    required
                    label="Seal Number"
                    value={sealNumber}
                    onChange={(e) => setSealNumber(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Seal Color</InputLabel>
                    <Select
                      value={sealColor}
                      onChange={(e) => setSealColor(e.target.value)}
                      label="Seal Color"
                    >
                      <MenuItem value="green">Green</MenuItem>
                      <MenuItem value="red" disabled={roleName !== 'admin'}>Red</MenuItem>
                      <MenuItem value="blue" disabled={roleName !== 'admin'}>Blue</MenuItem>
                      <MenuItem value="yellow" disabled={roleName !== 'admin'}>Yellow</MenuItem>
                      <MenuItem value="white" disabled={roleName !== 'admin'}>White</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveSealModalOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSealSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Remove Seal'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Vessel History Dialog */}
        <Dialog
          open={historyModalOpen}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            setHistoryModalOpen(false);
          }}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            Vessel History
            <IconButton
              onClick={() => setHistoryModalOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : vesselHistory.length > 0 ? (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Staff Member</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vesselHistory.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {format(entry.timestamp, 'MM/dd/yyyy hh:mm a')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={entry.type === 'launch' ? <WaterIcon /> : <AnchorIcon />}
                            label={entry.type === 'launch' ? 'Launch' : 'Haul'}
                            color={entry.type === 'launch' ? 'primary' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{entry.staffName || 'Unknown'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            <strong>Seal:</strong> {entry.sealNumber} ({entry.sealColor})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Trailer:</strong> {entry.trailerLicense}
                          </Typography>
                          {entry.notes && (
                            <Typography variant="body2">
                              <strong>Notes:</strong> {entry.notes}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" my={4}>
                <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No History Records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No history records found for this vessel
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Add Warning Dialog */}
        <Dialog
          open={warningDialog.open}
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return;
            setWarningDialog({ open: false, message: '' });
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            Warning
            <IconButton
              onClick={() => setWarningDialog({ open: false, message: '' })}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" color="error" sx={{ mt: 2 }}>
              {warningDialog.message}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setWarningDialog({ open: false, message: '' })}
              variant="contained"
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Add existing customer confirmation dialog */}
        <Dialog
          open={existingCustomerDialog.open}
          onClose={() => setExistingCustomerDialog({ open: false, existingCustomer: null, onConfirm: () => {} })}
        >
          <DialogTitle>Existing Customer Found</DialogTitle>
          <DialogContent>
            <Typography>
              A customer with the name {existingCustomerDialog.existingCustomer?.firstName} {existingCustomerDialog.existingCustomer?.lastName} already exists.
              Would you like to use this existing customer instead of creating a new one?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setExistingCustomerDialog({ open: false, existingCustomer: null, onConfirm: () => {} })}
            >
              Create New Customer
            </Button>
            <Button
              onClick={existingCustomerDialog.onConfirm}
              variant="contained"
              color="primary"
            >
              Use Existing Customer
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default LaunchRampPage; 